import { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft, Save, Truck, User, Package, Hash, Search, X, CheckSquare, Square, Plus } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import api from '../../api/clients';
import { useClients } from '../../hooks/useClients';
import { useProducts } from '../../hooks/useProducts';
import { useDebounce } from '../../hooks/useDebounce';
import { formatCurrency } from '../../lib/utils';
import { z } from 'zod';

const shippingSchema = z.object({
  remitenteNombre: z.string().default('MI TECNO HOGAR'),
  remitenteDireccion: z.string().optional(),
  destinatarioNombre: z.string().min(2, 'El nombre del destinatario es requerido'),
  destinatarioDocumento: z.string().optional(),
  destinatarioTelefono: z.string().optional(),
  destinatarioDireccion: z.string().min(5, 'La dirección del destinatario es requerida'),
  paqueteContenido: z.string().min(3, 'El contenido del paquete es requerido'),
  notasAdicionales: z.string().optional(),
  numeroSeguimiento: z.string().optional(),
});

type ShippingFormData = z.infer<typeof shippingSchema>;

interface ProductoSeleccionado {
  id: number;
  nombre: string;
  cantidad: number;
  precio: number;
}

export function ShippingForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  // Estados Cliente
  const [searchCliente, setSearchCliente] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState<any>(null);
  const [autocompletar, setAutocompletar] = useState(true);
  const clienteDropdownRef = useRef<HTMLDivElement>(null);

  // Estados Productos
  const [searchTerm, setSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [cantidad, setCantidad] = useState(1);
  const [productosSeleccionados, setProductosSeleccionados] = useState<ProductoSeleccionado[]>([]);
  const [autocompletarPaquete, setAutocompletarPaquete] = useState(true);
  const productDropdownRef = useRef<HTMLDivElement>(null);

  const { data: clientsData } = useClients({ limit: 1000 });
  const clients = clientsData?.data ?? [];
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const shouldSearchProducts =
    searchTerm.trim().length > 0 &&
    (!selectedProduct || selectedProduct.nombre !== searchTerm);

  const { data: productsData, isLoading: loadingProducts } = useProducts({
    search: shouldSearchProducts ? debouncedSearchTerm.trim() : undefined,
    limit: 20,
    activo: true,
  });

  const filteredProducts = productsData?.data ?? [];

  const { data: remito, isLoading } = useQuery({
    queryKey: ['shipping', id],
    queryFn: () => api.get(`/shipping/${id}`).then(res => res.data.data),
    enabled: isEditing,
  });

  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ShippingFormData>({
    resolver: zodResolver(shippingSchema),
    defaultValues: {
      remitenteNombre: 'MI TECNO HOGAR',
      remitenteDireccion: 'San Andrés de Giles, Buenos Aires, CP 6720',
      numeroSeguimiento: '',
      paqueteContenido: '',
    },
  });

  // Manejar clicks fuera de los dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clienteDropdownRef.current && !clienteDropdownRef.current.contains(event.target as Node)) {
        setShowClienteDropdown(false);
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target as Node)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cargar datos al editar
  useEffect(() => {
    if (remito && isEditing) {
      setValue('remitenteNombre', remito.remitenteNombre || 'MI TECNO HOGAR');
      setValue('remitenteDireccion', remito.remitenteDireccion || '');
      setValue('destinatarioNombre', remito.destinatarioNombre);
      setValue('destinatarioDocumento', remito.destinatarioDocumento || '');
      setValue('destinatarioTelefono', remito.destinatarioTelefono || '');
      setValue('destinatarioDireccion', remito.destinatarioDireccion);
      setValue('paqueteContenido', remito.paqueteContenido);
      setValue('notasAdicionales', remito.notasAdicionales || '');
      setValue('numeroSeguimiento', remito.numeroSeguimiento || '');

      if (remito.destinatarioNombre) {
        const cliente = clients.find((c: any) => c.nombre === remito.destinatarioNombre);
        if (cliente) {
          setClienteSeleccionado(cliente);
          setSearchCliente(cliente.nombre);
        }
      }

      setAutocompletarPaquete(false);
    }
  }, [remito, isEditing, setValue, clients]);

  useEffect(() => {
    if (autocompletarPaquete && productosSeleccionados.length > 0) {
      const contenidoGenerado = productosSeleccionados
        .map(p => `${p.cantidad}x ${p.nombre}`)
        .join(' - ');
      setValue('paqueteContenido', contenidoGenerado);
    } else if (autocompletarPaquete && productosSeleccionados.length === 0) {
      setValue('paqueteContenido', '');
    }
  }, [productosSeleccionados, autocompletarPaquete, setValue]);

  // Lógica Clientes
  const filteredClients = clients.filter((c: any) => {
    const searchLower = searchCliente.toLowerCase();
    const nombreMatch = c.nombre?.toLowerCase().includes(searchLower) || false;
    const telefonoMatch = c.telefono?.toLowerCase().includes(searchLower) || false;
    return nombreMatch || telefonoMatch;
  }) || [];

  const handleSelectClient = (client: any) => {
    setClienteSeleccionado(client);
    setSearchCliente(client.nombre);
    setShowClienteDropdown(false);

    setValue('destinatarioNombre', client.nombre);
    setValue('destinatarioDocumento', autocompletar ? (client.cuil || client.documento || '') : '');
    setValue('destinatarioTelefono', autocompletar ? (client.telefono || '') : '');
    setValue('destinatarioDireccion', autocompletar ? (client.direccion || '') : '');

    toast.success(`Cliente seleccionado: ${client.nombre}`);
  };

  const toggleAutocompletar = () => {
    const nuevoEstado = !autocompletar;
    setAutocompletar(nuevoEstado);

    if (nuevoEstado && clienteSeleccionado) {
      setValue('destinatarioDocumento', clienteSeleccionado.cuil || clienteSeleccionado.documento || '');
      setValue('destinatarioTelefono', clienteSeleccionado.telefono || '');
      setValue('destinatarioDireccion', clienteSeleccionado.direccion || '');
    }
  };

  const handleClearClient = () => {
    setClienteSeleccionado(null);
    setSearchCliente('');
    setValue('destinatarioNombre', '');
    setValue('destinatarioDocumento', '');
    setValue('destinatarioTelefono', '');
    setValue('destinatarioDireccion', '');
  };

  const handleSelectProduct = (product: any) => {
    setSelectedProduct(product);
    setSearchTerm(product.nombre);
    setShowProductDropdown(false);
  };

  const handleClearProduct = () => {
    setSelectedProduct(null);
    setSearchTerm('');
    setCantidad(1);
    setShowProductDropdown(false);
  };

  const handleAddProduct = () => {
    if (!selectedProduct) {
      toast.error('Selecciona un producto primero');
      return;
    }
    if (cantidad <= 0) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    const nuevoProducto: ProductoSeleccionado = {
      id: selectedProduct.id,
      nombre: selectedProduct.nombre,
      cantidad,
      precio: Number(selectedProduct.precio) || 0,
    };

    const existeIndex = productosSeleccionados.findIndex(p => p.id === nuevoProducto.id);
    if (existeIndex >= 0) {
      const actualizados = [...productosSeleccionados];
      actualizados[existeIndex].cantidad += cantidad;
      setProductosSeleccionados(actualizados);
      toast.success('Cantidad actualizada');
    } else {
      setProductosSeleccionados([...productosSeleccionados, nuevoProducto]);
      toast.success('Producto agregado al paquete');
    }

    // Resetear buscador
    setSelectedProduct(null);
    setSearchTerm('');
    setCantidad(1);
    setShowProductDropdown(false);
  };

  const handleRemoveProduct = (id: number) => {
    setProductosSeleccionados(productosSeleccionados.filter(p => p.id !== id));
  };

  const isFieldDisabled = !!clienteSeleccionado && autocompletar;

  const mutation = useMutation({
    mutationFn: (data: ShippingFormData) => {
      const endpoint = isEditing ? `/shipping/${id}` : '/shipping';
      const method = isEditing ? 'PUT' : 'POST';
      return api({ method, url: endpoint, data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping'] });
      toast.success(isEditing ? 'Remito actualizado' : 'Remito creado');
      navigate('/remitos');
    },
    onError: () => {
      toast.error('Error al guardar el remito');
    },
  });

  const onSubmit = (data: ShippingFormData) => {
    mutation.mutate(data);
  };

  if (isLoading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando remito...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/remitos')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {isEditing ? 'Editar Remito de Envío' : 'Nuevo Remito de Envío'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-xl shadow-sm p-6 max-w-3xl mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* Remitente */}
          <div className="md:col-span-2">
            <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Truck size={20} className="text-blue-600" />
              Remitente
            </h3>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
            <Input
              {...register('remitenteNombre')}
              placeholder="Nombre del remitente"
              error={errors.remitenteNombre?.message}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
            <Input
              {...register('remitenteDireccion')}
              placeholder="Dirección del remitente"
              error={errors.remitenteDireccion?.message}
            />
          </div>

          {/* Destinatario con buscador */}
          <div className="md:col-span-2 border-t pt-4 mt-2">
            <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <User size={20} className="text-green-600" />
              Destinatario
            </h3>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar Cliente Existente
            </label>
            <div className="relative" ref={clienteDropdownRef}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Buscar cliente por nombre o teléfono..."
                value={searchCliente}
                onChange={(e) => {
                  setSearchCliente(e.target.value);
                  setShowClienteDropdown(true);
                  if (!e.target.value) handleClearClient();
                }}
                onFocus={() => setShowClienteDropdown(true)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {searchCliente && (
                <button
                  type="button"
                  onClick={handleClearClient}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}

              {showClienteDropdown && searchCliente && filteredClients.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {filteredClients.map((client: any) => (
                    <button
                      key={client.id}
                      type="button"
                      className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                      onClick={() => handleSelectClient(client)}
                    >
                      <div>
                        <p className="font-medium text-gray-800">{client.nombre}</p>
                        {client.telefono && (
                          <p className="text-xs text-gray-400">{client.telefono}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Toggle autocompletar Cliente */}
          {clienteSeleccionado && (
            <div className="md:col-span-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <button
                type="button"
                onClick={toggleAutocompletar}
                className="flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800"
              >
                {autocompletar ? <CheckSquare size={18} className="text-blue-600" /> : <Square size={18} className="text-blue-400" />}
                {autocompletar ? 'Autocompletar datos activado' : 'Autocompletar datos desactivado'}
              </button>
            </div>
          )}

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
            <Input
              {...register('destinatarioNombre')}
              placeholder="Nombre del destinatario"
              error={errors.destinatarioNombre?.message}
              disabled={isFieldDisabled}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Documento</label>
            <Input
              {...register('destinatarioDocumento')}
              placeholder="DNI / CUIL"
              error={errors.destinatarioDocumento?.message}
              disabled={isFieldDisabled}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
            <Input
              {...register('destinatarioTelefono')}
              placeholder="Teléfono del destinatario"
              error={errors.destinatarioTelefono?.message}
              disabled={isFieldDisabled}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Dirección *</label>
            <Input
              {...register('destinatarioDireccion')}
              placeholder="Dirección completa del destinatario"
              error={errors.destinatarioDireccion?.message}
              disabled={isFieldDisabled}
            />
          </div>

          {/* Contenido del Paquete */}
          <div className="md:col-span-2 border-t pt-4 mt-2">
            <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package size={20} className="text-purple-600" />
                Contenido del Paquete
              </div>
            </h3>

            {/* Toggle Autocompletar / Manual para el paquete */}
            <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
              <button
                type="button"
                onClick={() => setAutocompletarPaquete(!autocompletarPaquete)}
                className="flex items-center gap-2 text-sm font-medium text-purple-700 hover:text-purple-800"
              >
                {autocompletarPaquete ? <CheckSquare size={18} className="text-purple-600" /> : <Square size={18} className="text-purple-400" />}
                {autocompletarPaquete
                  ? 'Modo Autocompletado (Buscar productos)'
                  : 'Modo Manual (Escribir contenido directamente)'}
              </button>
            </div>

            {/* Buscador de Productos */}
            {autocompletarPaquete && (
              <div className="flex flex-wrap gap-3 mb-4">
                <div className="relative flex-1 min-w-50" ref={productDropdownRef}>
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    type="text"
                    placeholder="Buscar producto para agregar al paquete..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setShowProductDropdown(true);
                      if (selectedProduct && e.target.value !== selectedProduct.nombre) {
                        setSelectedProduct(null);
                      }
                    }}
                    onFocus={() => setShowProductDropdown(true)}
                    className="pl-10 pr-10"
                  />

                  {selectedProduct && (
                    <button
                      type="button"
                      onClick={handleClearProduct}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Quitar producto seleccionado"
                    >
                      <X size={16} />
                    </button>
                  )}

                  {/* Dropdown de Productos */}
                  {showProductDropdown && searchTerm && filteredProducts.length > 0 && (
                    <div className="absolute left-0 right-0 z-9999 mt-1 bg-white border border-gray-200 rounded-lg shadow-2xl max-h-48 overflow-y-auto">
                      {filteredProducts.map((product: any) => {
                        const truncateDesc = (text: string) => text?.length > 25 ? text.substring(0, 25) + '...' : text;
                        return (
                          <button
                            key={product.id}
                            type="button"
                            className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition-colors flex justify-between items-center border-b border-gray-100 last:border-0"
                            onClick={() => handleSelectProduct(product)}
                          >
                            <div className="flex-1 min-w-0 pr-3">
                              <p className="font-medium text-gray-800 truncate">{product.nombre}</p>
                              {product.descripcion && (
                                <p className="text-xs text-gray-400 truncate">{truncateDesc(product.descripcion)}</p>
                              )}
                            </div>
                            <span className="text-sm font-semibold text-blue-600 shrink-0">
                              {formatCurrency ? formatCurrency(product.precio) : `$${product.precio}`}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {showProductDropdown && searchTerm && loadingProducts && (
                    <div className="absolute left-0 right-0 z-9999 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                      Buscando productos...
                    </div>
                  )}

                  {showProductDropdown && searchTerm && !loadingProducts && filteredProducts.length === 0 && (
                    <div className="absolute left-0 right-0 z-9999 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
                      No se encontraron productos
                    </div>
                  )}
                </div>

                <div className="w-20">
                  <Input
                    type="number"
                    value={cantidad}
                    onChange={(e) => setCantidad(Number(e.target.value) || 1)}
                    min={1}
                  />
                </div>

                <Button
                  type="button"
                  onClick={handleAddProduct}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Plus size={18} />
                  Agregar
                </Button>
              </div>
            )}

            {/* Lista de productos agregados temporalmente */}
            {autocompletarPaquete && productosSeleccionados.length > 0 && (
              <div className="space-y-2 mb-4 bg-gray-50 p-3 rounded-lg border border-gray-100">
                <p className="text-xs font-semibold text-gray-500 uppercase">Productos a enviar:</p>
                {productosSeleccionados.map((prod) => (
                  <div key={prod.id} className="flex justify-between items-center bg-white p-2 rounded border border-gray-200">
                    <span className="text-sm font-medium text-gray-700">
                      {prod.cantidad}x {prod.nombre}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveProduct(prod.id)}
                      className="text-red-500 hover:bg-red-50 p-1 rounded"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Textarea Autocompletado */}
            <div className="mt-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción Final del Contenido *
              </label>
              <textarea
                {...register('paqueteContenido')}
                rows={3}
                disabled={autocompletarPaquete}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  autocompletarPaquete ? 'bg-gray-100 border-gray-200 text-gray-600' : 'bg-white border-gray-300'
                }`}
                placeholder="Descripción detallada del contenido del paquete..."
              />
              {errors.paqueteContenido && (
                <p className="text-sm text-red-500 mt-1">{errors.paqueteContenido.message}</p>
              )}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas Adicionales</label>
            <textarea
              {...register('notasAdicionales')}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Instrucciones especiales o notas adicionales..."
            />
          </div>

          {/* Seguimiento */}
          <div className="md:col-span-2 border-t pt-4 mt-2">
            <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Hash size={20} className="text-orange-600" />
              Seguimiento
            </h3>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Número de Seguimiento
            </label>
            <Input
              {...register('numeroSeguimiento')}
              placeholder="Ej: TRACK-123456789"
              error={errors.numeroSeguimiento?.message}
            />
            <p className="text-xs text-gray-400 mt-1">
              Código de seguimiento para que el cliente pueda rastrear su paquete
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6 pt-6 border-t">
          <Button type="submit" disabled={isSubmitting}>
            <Save size={18} />
            {isSubmitting ? 'Guardando...' : isEditing ? 'Actualizar Remito' : 'Crear Remito'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/remitos')}
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
}